import { Form, useAppForm } from "@birdwatch/ui/form";
import { toast } from "@birdwatch/ui/toast";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { useEffect, useState } from "react";
import { z } from "zod";
import { BwHomeLine, BwLoadingLine } from "@/components/ui/icons";
import { APP_ROUTERS } from "@/constants/app-routers";
import {
  NoCoverageContent,
  PlaceAlreadyActiveWarning,
  UnverifiedAddressWarning,
} from "@/features/my/places/shell";
import { PRESALE_CAMPAIGN, usePresaleResume } from "@/features/shared/presale";
import { useSupportChat } from "@/features/shared/support-chat";
import {
  AddressSearchField,
  type PlaceAddressFormValues,
  placeAddressSchema,
  StateField,
  TextField,
} from "@/fields";
import { PRESALE_EVENTS, useAnalytics } from "@/libs/analytics";
import { usePresaleMembership } from "./_api/use-presale-membership";
import { usePresaleOfferTarget } from "./_api/use-presale-offer-target";
import { usePresaleOffers } from "./_api/use-presale-offers";
import {
  type PresaleAddress,
  type PresaleAddressOutcome,
  usePresalePlace,
} from "./_api/use-presale-place";
import { PresaleFunnelLayout } from "./_components/funnel/presale-funnel-layout";
import { getPresaleAnalyticsProps } from "./_libs/presale-analytics-props";
import {
  keepCampaignSearch,
  type PresaleCampaignSearch,
} from "./_libs/presale-funnel";
import { selectPresaleOffer } from "./_libs/presale-offer";

/** The shared place rules plus the coordinates only this funnel carries. */
const addressSchema = placeAddressSchema.extend({
  latitude: z.string(),
  longitude: z.string(),
});

/**
 * Every conclusion that keeps the buyer on this step. `eligible` is absent because it
 * navigates away — one slot for a four-state concept, so no impossible combination of
 * banners can be on screen at once.
 */
type PresalePlaceResult = Exclude<PresaleAddressOutcome, { kind: "eligible" }>;

const EMPTY_ADDRESS: PresaleAddress = {
  streetAddress: "",
  streetAddressLine2: "",
  city: "",
  state: "",
  zip: "",
  latitude: "",
  longitude: "",
};

/**
 * Step 2 — Add Place.
 *
 * The zipcode collected here is the campaign gate (Slack 2026-08-26: "route to this
 * page based on zipcode list… when the user adds the place"). A qualifying address is
 * created through `add-my-place`, which writes the `Place`, the `UserPlace` link AND
 * the purchase intent for the campaign's cart in one transaction — so step 3 opens
 * against a real intent rather than an invented one.
 */
export function PresalePlacePage({
  search,
}: {
  search: PresaleCampaignSearch;
}) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, user } = useUser();
  const { offers } = usePresaleOffers();
  const { target, isLoading: isLoadingTarget } = usePresaleOfferTarget(
    search.checkoutLinkId,
  );
  const offer = selectPresaleOffer(offers, target, search.checkoutLinkId);
  const place = usePresalePlace();
  const { sendEvent } = useAnalytics();
  // The funnel is a public route, so no session was ever identified here —
  // `show()` boots an anonymous one first. See `useSupportChat`.
  const { show: handleContactSupport } = useSupportChat();
  // The plan read on every step, so `planKey` / `planValue` are ONE value across the whole
  // funnel: `offer.lines[].key` is `Product.key`, a different key space from `Plan.key`.
  const { data: membership } = usePresaleMembership(search.checkoutLinkId);
  const [outcome, setOutcome] = useState<PresalePlaceResult | null>(null);
  const [joinedWaitlist, setJoinedWaitlist] = useState(false);

  // The address step needs a session — `add-my-place` reads the user from the token.
  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    navigate({
      to: APP_ROUTERS.PRESALE_SIGN_UP,
      search: keepCampaignSearch(search),
      replace: true,
    });
  }, [isLoaded, isSignedIn]);

  const { resume, isResolving } = usePresaleResume();

  /**
   * A reservation already exists — step 2 is done, so go finish paying for it. This is the
   * ONE place the funnel resumes from: all three post-auth paths (`presale-sign-up-page`'s
   * signed-in effect and its ticket branch, `presale-verify-page`'s OTP branch) land here,
   * as does a bookmark straight to this step.
   *
   * The intent's own values spread LAST, so they beat the URL's: the intent is what
   * `POST /checkout/pay` will actually charge, and a stale campaign param must not
   * re-point the cart to a tier the buyer never reserved.
   */
  useEffect(() => {
    if (!resume) return;
    navigate({
      to: APP_ROUTERS.PRESALE_CHECKOUT,
      search: { ...keepCampaignSearch(search), ...resume },
      replace: true,
    });
  }, [resume]);

  const form = useAppForm({
    defaultValues: EMPTY_ADDRESS,
    validators: { onBlur: addressSchema },
    onSubmit: async ({ value }) => {
      try {
        // A standing `unrecognized` banner means these exact values were already checked
        // and the buyer is pressing "Yes, confirm address" — any edit would have cleared it.
        const overriding = outcome?.kind === "unrecognized";
        const result = overriding
          ? place.confirmUnrecognized(value)
          : await place.check(value);

        // One send for every outcome, before any branch returns. `unrecognized`
        // carries no address, so fall back to what was typed; the others carry the
        // address Google standardized, whose own zip is `outside-campaign.zip`.
        const entered = result.kind === "unrecognized" ? value : result.address;
        sendEvent(PRESALE_EVENTS.ADDRESS_ENTERED, {
          ...getPresaleAnalyticsProps({ search, membership, offer }),
          stepId: "address_check",
          stepName: "Address Check",
          // An override reports `false` even though it goes on to reserve — the address
          // never did validate, and that is what the funnel report needs to see.
          addressValid: !overriding && result.kind !== "unrecognized",
          // The presale gate is the CAMPAIGN's zipcode list, not the platform's
          // `isInServiceArea` (see `usePresalePlace`) — but the funnel meaning of Join's
          // `inServiceArea`, "this address can buy here", is the same.
          inServiceArea: result.kind === "eligible",
          // One property covers every conclusion, so a fifth needs no edit here — and it
          // is the only place the duplicate case is visible in the funnel report.
          addressOutcome: result.kind,
          address1: entered.streetAddress,
          address2: entered.streetAddressLine2,
          city: entered.city,
          state: entered.state,
          zipCode: entered.zip,
        });

        if (result.kind !== "eligible") {
          setOutcome(result);
          return;
        }

        const created = await place.reserve(result.address, {
          checkoutLinkId: search.checkoutLinkId ?? offer?.checkoutLinkId,
          discountCode: search.discountCode,
          referId: search.referId,
          referredBy: search.referredBy,
        });

        navigate({
          to: APP_ROUTERS.PRESALE_CHECKOUT,
          search: {
            ...keepCampaignSearch(search),
            checkoutLinkId:
              search.checkoutLinkId ?? offer?.checkoutLinkId ?? undefined,
            purchaseIntentId: created.purchaseIntentId,
          },
        });
      } catch (error) {
        toast.error({
          title:
            error instanceof Error && error.message.trim()
              ? error.message
              : "We couldn't add that home.",
        });
      }
    },
  });

  /** Only overwrite what Google resolved — a partial suggestion must not wipe typing. */
  const applyAddress = (address: PlaceAddressFormValues) => {
    for (const [field, value] of Object.entries(address)) {
      if (!value) continue;
      form.setFieldValue(field as keyof PresaleAddress, value);
    }
  };

  const values = useStore(form.store, (state) => state.values);

  /**
   * Any edit invalidates what the last check concluded — and it is what makes the override
   * safe: an `unrecognized` outcome surviving to the next submit proves the values on
   * screen are the ones that were checked. `form.reset` is deliberately not used here;
   * `useAppForm` documents that a reset to values other than `defaultValues` is undone a
   * render later.
   */
  useEffect(() => {
    setOutcome(null);
  }, [values]);

  // Holding the form back until the resume decision lands does two things: the buyer never
  // sees the address form flash before being bounced, and a late-resolving resume can no
  // longer yank someone off the out-of-area waitlist screen below.
  //
  // `resume` is in the condition too, not just `isResolving`: the redirect runs in an
  // effect, which fires AFTER the commit, so the form would otherwise paint for one frame.
  if (isResolving || resume) {
    return (
      <PresaleFunnelLayout step="place">
        <div className="flex justify-center py-24">
          <BwLoadingLine size={28} className="animate-spin text-text-primary" />
        </div>
      </PresaleFunnelLayout>
    );
  }

  if (outcome?.kind === "outside-campaign") {
    const email = user?.primaryEmailAddress?.emailAddress ?? "";
    const { address, zip } = outcome;
    return (
      <PresaleFunnelLayout step="place">
        <NoCoverageContent
          addressLine1={[address.streetAddress, address.streetAddressLine2]
            .filter(Boolean)
            .join(", ")}
          addressLine2={[address.city, address.state, zip]
            .filter(Boolean)
            .join(", ")}
          canJoin={!!email}
          isJoining={place.isJoiningWaitlist}
          isJoined={joinedWaitlist}
          onBack={() => setOutcome(null)}
          onJoinWaitlist={async () => {
            try {
              await place.waitlist(email, address);
              sendEvent(PRESALE_EVENTS.WAITLIST_JOINED, {
                ...getPresaleAnalyticsProps({ search, membership, offer }),
                stepId: "waitlist",
                stepName: "Waitlist",
                emailCaptured: email,
                address1: address.streetAddress,
                address2: address.streetAddressLine2,
                city: address.city,
                state: address.state,
                zipCode: zip,
              });
              setJoinedWaitlist(true);
            } catch {
              toast.error({ title: "Can't join waitlist!" });
            }
          }}
        />
      </PresaleFunnelLayout>
    );
  }

  return (
    <PresaleFunnelLayout
      step="place"
      title="Which home are we caring for?"
      description={`Founding Memberships are limited to ${PRESALE_CAMPAIGN.marketName}, so we’ll check your ZIP as you go.`}
    >
      <Form form={form}>
        <div className="w-full p-3 md:p-4 rounded-xl shadow-md border border-border-dark-low text-left">
          <div className="flex items-center gap-2 text-sm border-b border-border-dark-low pb-2">
            <BwHomeLine size={24} className="text-text-primary shrink-0" />
            <p>Your service address</p>
          </div>
          <div className="mt-3 space-y-4">
            <AddressSearchField
              name="streetAddress"
              label="Street address"
              onAddressSelect={applyAddress}
            />
            <TextField
              name="streetAddressLine2"
              label="Apt, suite, unit (if applicable)"
              autoComplete="address-line2"
              placeholder="e.g., Unit 1A"
              optional
            />
            <TextField
              name="city"
              label="City / town"
              placeholder="City"
              autoComplete="address-level2"
            />
            <div className="grid grid-cols-2 gap-4">
              <StateField name="state" label="State" />
              <TextField
                name="zip"
                label="ZIP code"
                placeholder="20147"
                autoComplete="postal-code"
              />
            </div>
          </div>
        </div>
        {outcome?.kind === "unrecognized" && (
          <UnverifiedAddressWarning className="mt-6" />
        )}
        {outcome?.kind === "already-active" && (
          <PlaceAlreadyActiveWarning
            className="mt-6"
            onContactSupport={handleContactSupport}
          />
        )}
        <form.SubmitButton
          size="xl"
          className="w-full mt-6"
          // A home that already has an active membership cannot buy a founding one, so
          // support is the only way forward — nothing on this form can fix it.
          disabled={outcome?.kind === "already-active"}
          // `isLoadingTarget` is load-bearing, not cosmetic: `onSubmit` writes the
          // resolved `offer.checkoutLinkId` onto the real purchase intent, so
          // reserving before the link's tier resolves would bind the fallback offer.
          isPending={place.isChecking || place.isAdding || isLoadingTarget}
        >
          {outcome?.kind === "unrecognized"
            ? "Yes, confirm address"
            : "Continue"}
        </form.SubmitButton>
      </Form>
    </PresaleFunnelLayout>
  );
}
